import { useState } from 'react';

import { EventUserSchema } from 'types';
import { MAX_STAGE_SEATS } from 'utils';
import { filledSeatsCountSelector, isHandRaiseEnabledSelector } from 'store/selectors';
import { LoggerService } from 'services';
import { useStore, useToast } from 'hooks';
import { useLiveEvent } from 'containers/LiveEvent';
import { Divider, Switch } from 'components';

import Request from './HandRaises.Request';

import styles from './HandRaises.module.scss';

export type HandRaisesProps = {
  requests?: EventUserSchema[];
  onApprove?(username: string): Promise<void>;
  onReject?(username: string): Promise<void>;
  onEnable?(): Promise<void>;
  onDisable?(): Promise<void>;
};

const HandRaises = ({ requests = [], onApprove, onReject, onEnable, onDisable }: HandRaisesProps): JSX.Element => {
  const toast = useToast();
  const liveEvent = useLiveEvent();
  const { filledSeatsCount, isHandRaiseEnabled } = useStore(state => ({
    filledSeatsCount: filledSeatsCountSelector(liveEvent.eventCode)(state),
    isHandRaiseEnabled: isHandRaiseEnabledSelector(liveEvent.eventCode)(state)
  }));

  const [toggling, setToggling] = useState(false);

  const handleOnToggle = async () => {
    try {
      const prevValue = isHandRaiseEnabled;
      const newValue = !prevValue;

      setToggling(true);

      if (newValue) {
        await onEnable?.();
      } else {
        await onDisable?.();
      }

      setToggling(false);
    } catch (e) {
      LoggerService.log('TOGGLE_HAND_RAISE_PERMISSION', e);
    }
  };

  const handleOnApprove = async (username: string) => {
    try {
      if (filledSeatsCount >= MAX_STAGE_SEATS) {
        toast.info('No seats are available on stage. Please remove someone from the active stage to invite a speaker', {
          id: 'NO_AVAILABLE_SEATS_ON_STAGE'
        });
      } else {
        await onApprove?.(username);
      }
    } catch (e) {
      LoggerService.log('APPROVE_HAND_RAISE', e);
    }
  };

  const handleOnReject = async (username: string) => {
    try {
      await onReject?.(username);
    } catch (e) {
      LoggerService.log('REJECT_HAND_RAISE', e);
    }
  };

  return (
    <div className={styles.container}>
      <Switch
        className={styles.permission}
        title="Open for raising hands"
        disabled={toggling}
        checked={isHandRaiseEnabled}
        onChange={handleOnToggle}
      />

      {requests.length > 0 ? (
        <>
          <Divider className={styles.separator} />
          <ul className={styles.list}>
            {requests.map(request => (
              <li key={request.username}>
                <Request user={request} onApprove={handleOnApprove} onReject={handleOnReject} />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className={styles.empty}>{isHandRaiseEnabled ? 'List is empty' : 'Hand raises are turned off'}</p>
      )}
    </div>
  );
};

export default HandRaises;
